import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Card } from '../components/ui/card';
import { ArrowLeft, ArrowRight, HelpCircle } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useLang } from '../context/LangContext';
import { MicButton } from '../components/MicButton';

const STEP_FIELDS = [
  ['in_canada', 'province', 'city', 'postal_code', 'age', 'marital_status'],
  ['employed', 'is_student', 'annual_income', 'num_children', 'children_with_disability', 'housing', 'filed_tax_return'],
  ['has_disability', 'disability_types', 'uses_mobility_equipment', 'prevents_regular_work', 'has_private_dental'],
  ['on_odsp', 'dtc_status'],
];

const DISABILITY_TYPES = [
  'Physical disability', 'Mobility difficulty', 'Vision impairment', 'Hearing impairment',
  'Mental-health condition', 'Developmental disability', 'Cognitive impairment', 'Chronic medical condition', 'Multiple disabilities', 'Other',
];

export default function Questionnaire() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState({
    in_canada: null, province: 'Ontario', city: '', postal_code: '', age: null, marital_status: '',
    num_children: 0, children_with_disability: false, is_student: false, employed: false,
    annual_income: null, filed_tax_return: null, has_disability: null, disability_types: [],
    uses_mobility_equipment: null, prevents_regular_work: null, dtc_status: 'none', on_odsp: false,
    housing: '', has_private_dental: null,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/profile').then(({ data }) => {
      if (data && Object.keys(data).length) setProfile((p) => ({ ...p, ...data }));
    }).catch(() => {});
  }, []);

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const [voiceNote, setVoiceNote] = useState('');
  const voiceAnswer = async (transcript) => {
    setVoiceNote(t('Understanding…'));
    try {
      const { data } = await api.post('/questionnaire/voice', { transcript, fields: STEP_FIELDS[step], current: profile });
      const keys = Object.keys(data.values);
      if (!keys.length) { setVoiceNote(''); return toast.error(t('I heard you, but could not match it to a question on this step. Try again or tap the buttons.')); }
      setProfile((p) => ({ ...p, ...data.values }));
      setVoiceNote(`${t('Heard')}: “${transcript}” → ${keys.length} ${t('answer(s) filled')}`);
      toast.success(`${keys.length} ${t('answer(s) filled from your voice')}`);
    } catch (e) { setVoiceNote(''); toast.error(e.response?.data?.detail || t('Could not understand the answer')); }
  };
  const tr = t;
  const toggleType = (t) => setProfile((p) => ({
    ...p,
    disability_types: p.disability_types.includes(t) ? p.disability_types.filter((x) => x !== t) : [...p.disability_types, t],
  }));

  const steps = [
    {
      title: t('A little about you'),
      body: (
        <div className="space-y-6">
          <Field label={t('Do you live in Canada?')} hint={t('We need this to know which federal benefits apply.')}>
            <TriChoice value={profile.in_canada} onChange={(v) => update('in_canada', v)} testPrefix="in-canada" />
          </Field>
          <Field label={t('Which province?')}>
            <Input value={profile.province} onChange={(e) => update('province', e.target.value)} className="h-11" data-testid="q-province" />
          </Field>
          <Field label={t('City / town (optional)')}>
            <Input value={profile.city} onChange={(e) => update('city', e.target.value)} className="h-11" data-testid="q-city" placeholder="e.g. Toronto" />
          </Field>
          <Field label={t('Postal code (optional)')} hint={t('First 3 characters is enough. Unlocks municipal housing, transit and paratransit programs.')}>
            <Input value={profile.postal_code || ''} onChange={(e) => update('postal_code', e.target.value.toUpperCase())} maxLength={7} className="h-11 max-w-[200px] uppercase" data-testid="q-postal" placeholder="e.g. M5V" />
          </Field>
          <Field label={t('Your age')}>
            <Input type="number" min={0} max={120} value={profile.age ?? ''} onChange={(e) => update('age', e.target.value ? Number(e.target.value) : null)} className="h-11 max-w-[160px]" data-testid="q-age" />
          </Field>
        </div>
      ),
    },
    {
      title: t('Household & work'),
      body: (
        <div className="space-y-6">
          <Field label={t('Are you currently employed?')}><TriChoice value={profile.employed} onChange={(v) => update('employed', v)} testPrefix="employed" /></Field>
          <Field label={t('Approximate annual household income (CAD)')}>
            <div className="flex flex-wrap gap-2">
              {[['Under $20,000', 15000], ['$20,000 – $40,000', 30000], ['$40,000 – $60,000', 50000], ['$60,000 – $90,000', 75000], ['Over $90,000', 120000]].map(([l, v]) => (
                <Button key={v} type="button" variant={profile.annual_income === v ? 'default' : 'outline'} className={profile.annual_income === v ? 'bg-primary text-primary-foreground' : ''} onClick={() => update('annual_income', v)} data-testid={`q-income-${v}`}>{t(l)}</Button>
              ))}
            </div>
          </Field>
          <Field label={t('How many children live with you?')}>
            <Input type="number" min={0} max={12} value={profile.num_children} onChange={(e) => update('num_children', Number(e.target.value))} className="h-11 max-w-[120px]" data-testid="q-num-children" />
          </Field>
          {profile.num_children > 0 && (
            <Field label={t('Does any child have a disability?')}><TriChoice value={profile.children_with_disability} onChange={(v) => update('children_with_disability', v)} testPrefix="child-disability" /></Field>
          )}
          <Field label={t('Do you rent, own, or something else?')}>
            <div className="flex flex-wrap gap-2">
              {['rent', 'own', 'homeless', 'other'].map((h) => (
                <Button key={h} type="button" variant={profile.housing === h ? 'default' : 'outline'} className={`capitalize ${profile.housing === h ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => update('housing', h)} data-testid={`q-housing-${h}`}>{t(h)}</Button>
              ))}
            </div>
          </Field>
          <Field label={t('Did you file your most recent tax return?')}><TriChoice value={profile.filed_tax_return} onChange={(v) => update('filed_tax_return', v)} testPrefix="tax-filed" /></Field>
        </div>
      ),
    },
    {
      title: t('Health & disability'),
      body: (
        <div className="space-y-6">
          <Field label={t('Do you have a disability or health condition that affects your daily life?')}><TriChoice value={profile.has_disability} onChange={(v) => update('has_disability', v)} testPrefix="has-disability" /></Field>
          {profile.has_disability && (
            <Field label={t('Which best describes your condition? (Select all that apply)')}>
              <div className="grid sm:grid-cols-2 gap-2">
                {DISABILITY_TYPES.map((t) => (
                  <label key={t} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${profile.disability_types.includes(t) ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary'}`}>
                    <Checkbox checked={profile.disability_types.includes(t)} onCheckedChange={() => toggleType(t)} data-testid={`q-dtype-${t.replace(/\s+/g, '-').toLowerCase()}`} />
                    <span className="text-sm">{tr(t)}</span>
                  </label>
                ))}
              </div>
            </Field>
          )}
          <Field label={t('Do you use mobility equipment (wheelchair, walker, scooter)?')}><TriChoice value={profile.uses_mobility_equipment} onChange={(v) => update('uses_mobility_equipment', v)} testPrefix="mobility" /></Field>
          <Field label={t('Does your condition prevent you from working regularly?')}><TriChoice value={profile.prevents_regular_work} onChange={(v) => update('prevents_regular_work', v)} testPrefix="prevents-work" /></Field>
          <Field label={t('Do you have private dental insurance?')}><TriChoice value={profile.has_private_dental} onChange={(v) => update('has_private_dental', v)} testPrefix="dental" /></Field>
        </div>
      ),
    },
    {
      title: t('Existing benefits'),
      body: (
        <div className="space-y-6">
          <Field label={t('Do you currently receive ODSP?')}><TriChoice value={profile.on_odsp} onChange={(v) => update('on_odsp', v)} testPrefix="on-odsp" /></Field>
          <Field label={t('What is your Disability Tax Credit (DTC) status?')}>
            <RadioGroup value={profile.dtc_status} onValueChange={(v) => update('dtc_status', v)}>
              {[['approved', 'DTC approved'], ['applied', 'Applied / in progress'], ['none', 'Not applied yet'], ['unsure', "I'm not sure"]].map(([v, l]) => (
                <label key={v} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${profile.dtc_status === v ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary'}`}>
                  <RadioGroupItem value={v} data-testid={`q-dtc-${v}`} />
                  <span className="text-sm">{t(l)}</span>
                </label>
              ))}
            </RadioGroup>
          </Field>
        </div>
      ),
    },
  ];

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/match', profile);
      sessionStorage.setItem('match_results', JSON.stringify(data.results));
      navigate('/results');
    } catch (e) {
      toast.error('Could not calculate matches. Please try again.');
    } finally { setSubmitting(false); }
  };

  const pct = ((step + 1) / steps.length) * 100;
  const cur = steps[step];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-muted-foreground">{t('Step')} {step + 1} {t('of')} {steps.length}</div>
          <div className="text-sm font-medium text-primary">{Math.round(pct)}%</div>
        </div>
        <Progress value={pct} className="h-2" data-testid="q-progress" />
      </div>
      <Card className="p-6 sm:p-8 bg-card">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{fontFamily: 'Lexend'}}>{cur.title}</h1>
        <p className="text-sm text-muted-foreground mb-6 flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5" /> {t('Every question has an "I\'m not sure" option.')}</p>
        <div className="mb-6 rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3 flex items-center gap-3 flex-wrap" data-testid="voice-answer-bar">
          <MicButton label={t('Answer by voice')} testId="q-mic" onText={voiceAnswer} />
          <span className="text-xs text-muted-foreground flex-1 min-w-[200px]" data-testid="voice-answer-note">{voiceNote || t('Say it naturally, e.g. "I\'m 42, I live in Ottawa, postal code K1A, and I\'m single."')}</span>
        </div>
        {cur.body}
        <div className="mt-8 flex items-center justify-between">
          <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="q-back"><ArrowLeft className="w-4 h-4 mr-2" />{t('Back')}</Button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} data-testid="q-next" className="bg-primary hover:bg-primary/90 text-primary-foreground">{t('Next')} <ArrowRight className="w-4 h-4 ml-2" /></Button>
          ) : (
            <Button onClick={submit} disabled={submitting} data-testid="q-submit" className="bg-primary hover:bg-primary/90 text-primary-foreground">{submitting ? t('Matching…') : t('See my matches')} <ArrowRight className="w-4 h-4 ml-2" /></Button>
          )}
        </div>
      </Card>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-base font-semibold block mb-1">{label}</Label>
      {hint && <p className="text-xs text-muted-foreground mb-3">{hint}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function TriChoice({ value, onChange, testPrefix }) {
  const { t } = useLang();
  const opts = [[true, t('Yes')], [false, t('No')], [null, t("I'm not sure")]];
  return (
    <div className="flex flex-wrap gap-2">
      {opts.map(([v, l]) => (
        <Button
          key={String(v)}
          type="button"
          variant={value === v ? 'default' : 'outline'}
          className={value === v ? 'bg-primary text-primary-foreground' : ''}
          onClick={() => onChange(v)}
          data-testid={`${testPrefix}-${v === null ? 'unsure' : v ? 'yes' : 'no'}`}
        >{l}</Button>
      ))}
    </div>
  );
}
